import { useRef, useState } from "preact/hooks";
import { type Card, LIMITS, type ShareResponse } from "../../../../shared/types.ts";
import { absoluteTime, relativeTime } from "../lib.ts";
import { load, save, store, toast } from "../state.ts";
import { IconShareLink } from "./icons.tsx";

// Dono: B5. "Link temporário" (24 h) no detalhe: criar, copiar, revogar.
// O servidor não lista os links de um card (só guarda o hash do token): o endereço criado fica neste aparelho
// (localStorage "faig.shares"), o que basta para copiar e revogar daqui até vencer.

const KEY = "faig.shares";
type Shares = Record<string, ShareResponse>;
function readShares(): Shares {
  try {
    const all = JSON.parse(load(KEY) ?? "{}") as Shares;
    return Object.fromEntries(Object.entries(all).filter(([, s]) => s.expiresAt > Date.now()));
  } catch {
    return {};
  }
}
function writeShare(cardId: string, s: ShareResponse | null) {
  const all = readShares();
  if (s) all[cardId] = s;
  else delete all[cardId];
  save(KEY, Object.keys(all).length ? JSON.stringify(all) : null);
}

/** Depois de trocar o conteúdo, o foco vai para a ação principal (o botão clicado sumiu). */
const focusMain = (pop: HTMLElement | null) => requestAnimationFrame(() => pop?.querySelector<HTMLElement>(".share-actions .primary")?.focus());

/**
 * Abre por cima do botão (ou embaixo, sem espaço), dentro da janela. offsetWidth/Height e não getBoundingClientRect:
 * o pop entra com animação de escala e o retângulo viria encolhido (no celular o pop vazava da borda direita).
 */
function place(pop: HTMLElement, btn: HTMLElement) {
  const b = btn.getBoundingClientRect();
  const p = { width: pop.offsetWidth, height: pop.offsetHeight };
  const left = Math.min(Math.max(10, b.left + b.width / 2 - p.width / 2), innerWidth - p.width - 10);
  const top = b.top - p.height - 12 >= 10 ? b.top - p.height - 12 : Math.min(b.bottom + 12, innerHeight - p.height - 10);
  pop.style.left = `${left}px`;
  pop.style.top = `${Math.max(10, top)}px`;
}

export function ShareButton({ card }: { card: Card }) {
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [link, setLink] = useState<ShareResponse | null>(() => readShares()[card.id] ?? null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState("");
  const online = store.status.value.online;
  if (card.deletedAt) return null;
  const id = `share-${card.id}`;
  const live = link && link.expiresAt > Date.now() ? link : null;

  const create = () => {
    setBusy(true);
    store
      .share(card.id)
      .then(
        (s) => {
          writeShare(card.id, s);
          setLink(s);
          setSaid("Link criado.");
          requestAnimationFrame(() => pop.current && btn.current && place(pop.current, btn.current));
          focusMain(pop.current);
        },
        () => {},
      )
      .finally(() => setBusy(false));
  };
  const copy = (url: string) =>
    navigator.clipboard?.writeText(url).then(
      () => toast({ key: `share-copy:${card.id}`, text: "Link copiado. Vale por 24 horas." }),
      () => toast({ key: "share-copy-fail", text: "Não deu para copiar. Selecione o endereço e copie." }),
    );
  const revoke = (s: ShareResponse) => {
    setBusy(true);
    store
      .revokeShare(s.tokenId)
      .catch((e: Error) => {
        if (e.message !== "not_found") throw e; // já tinha vencido ou sido revogado: some daqui também
      })
      .then(
        () => {
          writeShare(card.id, null);
          setLink(null);
          setSaid("Link revogado. Quem tinha o endereço não vê mais o card.");
          focusMain(pop.current);
        },
        () => {},
      )
      .finally(() => setBusy(false));
  };

  return (
    <>
      <button ref={btn} type="button" class="round" popovertarget={id} aria-label="Link temporário" data-tip="Link temporário">
        <IconShareLink />
      </button>
      <div
        ref={pop}
        id={id}
        popover="auto"
        class="pop share-pop"
        role="dialog"
        aria-labelledby={`${id}-title`}
        onToggle={(e) => {
          if (e.newState !== "open") return setSaid("");
          setLink(readShares()[card.id] ?? null);
          place(e.currentTarget, btn.current!);
          focusMain(e.currentTarget); // autofocus não vale aqui: o detalhe já tem o foco
        }}
      >
        <p id={`${id}-title`} class="share-title">
          Link temporário
        </p>
        {live ? (
          <>
            <label class="share-label" for={`${id}-url`}>
              Endereço público
            </label>
            <input id={`${id}-url`} class="share-url" readOnly value={live.url} onFocus={(e) => e.currentTarget.select()} />
            <p class="share-meta">
              Vale até {absoluteTime(live.expiresAt)} ({relativeTime(live.expiresAt).replace("em ", "termina em ")}).
            </p>
            {card.isPrivate && <p class="share-warn">O card ficou privado: o link mostra “não encontrado” enquanto ele tiver #privado.</p>}
            <div class="share-actions">
              <button type="button" class="acc-btn primary" onClick={() => copy(live.url)}>
                Copiar link
              </button>
              <button type="button" class="acc-btn danger" disabled={busy || !online} onClick={() => revoke(live)}>
                Revogar
              </button>
            </div>
          </>
        ) : card.isPrivate ? (
          // Decisão O1: o servidor recusa (403); aqui o motivo aparece antes de tentar.
          <p class="share-text">Card privado não ganha link público. Para compartilhar, tire a etiqueta #privado.</p>
        ) : (
          <>
            <p class="share-text">Um endereço público só para este card. Vale por {LIMITS.shareLinkHours} horas, e você pode cortar antes.</p>
            <div class="share-actions">
              <button type="button" class="acc-btn primary" disabled={busy || !online} aria-busy={busy} onClick={create}>
                {busy ? "Criando…" : `Criar link de ${LIMITS.shareLinkHours} h`}
              </button>
            </div>
            {!online && <p class="share-meta">Criar link precisa de conexão.</p>}
          </>
        )}
        <p class="sr-only" role="status">
          {said}
        </p>
      </div>
    </>
  );
}
